import { useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  Building2, Copy, Download, Handshake, LogOut, Monitor, Moon, Rocket, Settings, Sun, UserRound, type LucideIcon,
} from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/stores/auth-store'
import { useUiStore, type ThemePref } from '@/stores/ui-store'
import { cn, initials } from '@/lib/utils'
import { accountSubtitle } from '@/lib/account-label'
import { ROLE_LABELS, avatarSrc } from '@/pages/settings/settings-utils'
import { usePresenceMap, usePresenceRealtime, useSetMyPresence } from '@/hooks/use-presence'
import { PRESENCE_DOT, PRESENCE_LABEL, PRESENCE_OPTIONS, isPresenceStatus, presenceOf } from '@/lib/presence'
import { apiError } from '@/lib/api-client'
import { useAffiliateMe } from '@/hooks/use-affiliate'
import { useInstallApp } from '@/components/shared/install-app'
import { showAffiliateMenu } from '@/lib/affiliate'
import { LanguageSubmenu } from '@/components/shared/language-select'

const THEME_OPTIONS: { id: ThemePref; icon: LucideIcon }[] = [
  { id: 'light', icon: Sun },
  { id: 'dark', icon: Moon },
  { id: 'system', icon: Monitor },
]

/**
 * Menu tài khoản ở đáy cột menu chính (kiểu SaleSmartly): avatar luôn hiện,
 * tên/vai trò theo `labelClassName` (ẩn khi cột hẹp, hiện khi bung).
 *
 * Bảng bật ra: đầu là ảnh + tên + email và bánh răng vào Cài đặt cá nhân
 * (/account); rồi Trạng thái trực tuyến (chấm màu trên avatar + menu con đổi
 * Trực tuyến / Bận / Ngoại tuyến); thông tin tổ chức (tên, chép ID); chủ đề; ngôn ngữ; đăng xuất.
 * Menu này luôn gắn trong khung ứng dụng nên cũng là nơi giữ kết nối realtime
 * (mở app = Trực tuyến).
 */

/** Chấm trạng thái đè góc dưới-phải avatar. */
function StatusDot({ className, status }: { className?: string; status: keyof typeof PRESENCE_DOT }) {
  return (
    <span
      className={cn('absolute bottom-0 right-0 block rounded-full ring-2 ring-background', PRESENCE_DOT[status], className)}
      aria-label={PRESENCE_LABEL[status]}
      title={PRESENCE_LABEL[status]}
    />
  )
}
export function UserMenu({ labelClassName }: { labelClassName?: string }) {
  const user = useAuthStore((s) => s.user)
  const { t } = useTranslation('shell')
  const logout = useAuthStore((s) => s.logout)
  const { themePref, setThemePref } = useUiStore()
  const navigate = useNavigate()
  const { data: affiliateMe } = useAffiliateMe()
  const installApp = useInstallApp()
  const loadMe = useAuthStore((s) => s.loadMe)
  usePresenceRealtime(user?.id)
  const presenceMap = usePresenceMap(!!user)
  const setPresence = useSetMyPresence()
  // Phiên "xem dưới quyền" không đổi trạng thái của nhân viên đó.
  const impersonating = !!user?.impersonatedBy

  // Đăng nhập / đăng ký trả user chưa kèm tổ chức → nạp một lần để dòng phụ có TÊN TỔ CHỨC của khách.
  const orgRequestedFor = useRef<string | null>(null)
  const needOrg = !!user && !user.org?.name
  const userId = user?.id
  useEffect(() => {
    if (!needOrg || !userId || orgRequestedFor.current === userId) return
    orgRequestedFor.current = userId
    void loadMe()
  }, [needOrg, userId, loadMe])

  if (!user) return null

  const myStatus = presenceOf(presenceMap.data, user.id)
  const changeStatus = (v: string) => {
    if (!isPresenceStatus(v) || v === myStatus) return
    setPresence.mutate(v, {
      onSuccess: () => toast.success(t('userMenu.statusChanged', { status: PRESENCE_LABEL[v] })),
      onError: (err) => toast.error(apiError(err)),
    })
  }

  const avatar = avatarSrc(user.avatarUrl)
  const orgId = user.org?.id ?? user.orgId
  const currentTheme = THEME_OPTIONS.find((o) => o.id === themePref) ?? THEME_OPTIONS[2]
  const ThemeIcon = currentTheme.icon

  const copyOrgId = () => {
    navigator.clipboard.writeText(orgId).then(
      () => toast.success(t('userMenu.orgIdCopied')),
      () => toast.error(t('userMenu.copyBlocked')),
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex w-full items-center gap-2.5 overflow-hidden rounded-lg px-1.5 py-1.5 text-left outline-none hover:bg-white/55 dark:hover:bg-white/5">
        <span className="relative shrink-0">
          <Avatar className="h-8 w-8">
            {avatar && <AvatarImage src={avatar} />}
            <AvatarFallback>{initials(user.fullName)}</AvatarFallback>
          </Avatar>
          <StatusDot status={myStatus} className="h-2.5 w-2.5" />
        </span>
        <div className={cn('min-w-0 flex-1', labelClassName)}>
          <p className="truncate text-sm font-medium leading-tight">{user.fullName}</p>
          <p className="truncate text-xs leading-tight text-muted-foreground">
            {accountSubtitle(user.org?.name, user.roleName || ROLE_LABELS[user.role] || user.role)}
          </p>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="end" sideOffset={8} className="w-72 p-1.5">
        {/* Đầu bảng: ảnh + tên + email, bánh răng vào Cài đặt cá nhân */}
        <div className="flex items-center gap-3 px-2 py-2">
          <span className="relative shrink-0">
            <Avatar className="h-10 w-10">
              {avatar && <AvatarImage src={avatar} />}
              <AvatarFallback>{initials(user.fullName)}</AvatarFallback>
            </Avatar>
            <StatusDot status={myStatus} className="h-3 w-3" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{user.fullName}</p>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
          </div>
          <DropdownMenuItem asChild className="shrink-0 p-1.5 text-muted-foreground focus:text-primary">
            <Link to="/account/profile" title={t('userMenu.personalSettings')} aria-label={t('userMenu.personalSettings')}>
              <Settings />
            </Link>
          </DropdownMenuItem>
        </div>

        <DropdownMenuSeparator />
        {/* Trạng thái trực tuyến (kiểu SaleSmartly) */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger disabled={impersonating}>
            <span className={cn('ml-0.5 mr-2.5 h-2.5 w-2.5 shrink-0 rounded-full', PRESENCE_DOT[myStatus])} />
            <span className="flex-1">{t('userMenu.status')}</span>
            <span className="pl-2 pr-1 text-xs text-muted-foreground">{PRESENCE_LABEL[myStatus]}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-64">
            <DropdownMenuRadioGroup value={myStatus} onValueChange={changeStatus}>
              {PRESENCE_OPTIONS.map((o) => (
                <DropdownMenuRadioItem key={o.id} value={o.id} disabled={setPresence.isPending}>
                  <span className={cn('mr-2 mt-1.5 h-2 w-2 shrink-0 self-start rounded-full', PRESENCE_DOT[o.id])} />
                  <span className="min-w-0">
                    <span className="block">{o.label}</span>
                    <span className="block text-xs text-muted-foreground">{o.hint}</span>
                  </span>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />
        {/* Tổ chức (workspace) đang làm việc */}
        <div className="flex items-center gap-2 px-2 py-1.5 text-sm">
          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="shrink-0 text-muted-foreground">{t('userMenu.org')}</span>
          <span className="ml-auto min-w-0 truncate font-medium" title={user.org?.name}>
            {user.org?.name ?? '—'}
          </span>
        </div>
        {orgId && (
          <DropdownMenuItem onSelect={copyOrgId} title={t('userMenu.copyOrgIdHint')}>
            <Copy className="text-muted-foreground" />
            <span className="shrink-0">{t('userMenu.orgId')}</span>
            <span className="ml-auto min-w-0 truncate font-mono text-xs text-muted-foreground">
              {orgId.slice(0, 8)}…
            </span>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <ThemeIcon className="mr-2 h-4 w-4 text-muted-foreground" />
            <span className="flex-1">{t('userMenu.theme')}</span>
            <span className="pl-2 pr-1 text-xs text-muted-foreground">{t(`userMenu.themes.${currentTheme.id}`)}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-44">
            <DropdownMenuRadioGroup value={themePref} onValueChange={(v) => setThemePref(v as ThemePref)}>
              {THEME_OPTIONS.map((o) => (
                <DropdownMenuRadioItem key={o.id} value={o.id}>
                  {t(`userMenu.themes.${o.id}`)}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <LanguageSubmenu />
        <DropdownMenuItem onSelect={() => navigate('/account/profile')}>
          <UserRound className="text-muted-foreground" /> {t('userMenu.personalSettings')}
        </DropdownMenuItem>
        {/* Đã cài rồi (đang chạy như ứng dụng) thì ẩn. Cài một chạm nếu trình duyệt hỗ trợ, không thì mở hướng dẫn. */}
        {installApp.visible && (
          <DropdownMenuItem onSelect={() => void installApp.run()}>
            <Download className="text-muted-foreground" /> {t('userMenu.installApp')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => navigate('/onboarding')}>
          <Rocket className="text-muted-foreground" /> {t('userMenu.onboarding')}
        </DropdownMenuItem>
        {/* E2: chương trình tắt → ẩn với người chưa là đối tác (đối tác cũ vẫn thấy). */}
        {showAffiliateMenu(affiliateMe) && (
          <DropdownMenuItem onSelect={() => navigate('/affiliate')}>
            <Handshake className="text-muted-foreground" /> {t('userMenu.affiliate')}
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onSelect={async () => {
            await logout()
            navigate('/login')
          }}
        >
          <LogOut /> {t('userMenu.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
